import { Role } from "../../../domain/Role";
import { ProductAttribute } from "../../../domain/ProductAttribute";
import { Dropdown, Spinner, Typography } from "@optimizely/axiom";
import React, { useState } from "react";
import classnames from "classnames";
import { SearchInput } from "../SearchInput/SearchInput";
import { SEARCH_DROPDOWN_THRESHOLD } from "../../../constants/constants";

/*
TODO: To better follow the dropdown pattern - the value should be the `id` of the role which
should be looked up within the component to display as the `buttonContent`
 */
interface RoleSelectorProps {
    className?: string;
    roles?: Role[];
    fullWidth?: boolean;
    isDisabled?: boolean;
    loading?: boolean;
    onRoleChanged?: (role: Role) => void;
    placeholder?: string;
    preExistingRoleIds?: string[];
    project?: ProductAttribute;
    selectedRole?: Role;
    hideLabel?: boolean;
    isInitialized?: boolean;
}

export const RoleSelector = ({
    className,
    fullWidth = true,
    loading,
    selectedRole,
    roles,
    onRoleChanged,
    placeholder = "Select Role...",
    preExistingRoleIds = [],
    isDisabled = false,
    hideLabel = false,
    isInitialized = false
}: RoleSelectorProps) => {
    const [searchQuery, setSearchQuery] = useState("");

    const getRoleName = (role: Role) => {
        return role.displayName || role.name;
    };

    const filteredRoles =
        roles?.filter((role) => getRoleName(role).toLowerCase().includes(searchQuery.toLowerCase())) || [];

    const classes = classnames("admin-center__form-dropdown", className);

    let buttonContent: string | React.ReactNode = selectedRole ? getRoleName(selectedRole) : placeholder;
    if (!isInitialized && loading) {
        buttonContent = (
            <>
                <Spinner size="small" /> Loading roles...
            </>
        );
    } else if (isInitialized && !roles?.length) {
        buttonContent = "No roles available.";
    }

    const showSearch = !!roles?.length && roles.length > SEARCH_DROPDOWN_THRESHOLD;

    return (
        <div className={className}>
            {!hideLabel && (
                <Typography type="body" className="oui-label">
                    Role
                </Typography>
            )}
            <Dropdown
                data-cy="role-dropdown"
                isDisabled={isDisabled || !roles?.length}
                buttonContent={buttonContent}
                arrowIcon="down"
                fullWidth={fullWidth}
                className={classes}
                onClick={() => setSearchQuery("")}
            >
                <Dropdown.Contents>
                    {showSearch && (
                        <Dropdown.ListItem ignoreToggle>
                            <SearchInput
                                placeholder="Search roles..."
                                type="text"
                                className="admin-center__dropdown-search-list-item"
                                value={searchQuery}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onChange={({ value }) => setSearchQuery(value?.toLowerCase())}
                            />
                        </Dropdown.ListItem>
                    )}
                    {!filteredRoles?.length && searchQuery?.length > 0 && (
                        <Dropdown.ListItem onClick={() => setSearchQuery("")}>
                            <Typography type="body" className="push--sides">
                                No results matching your search
                            </Typography>
                        </Dropdown.ListItem>
                    )}
                    {filteredRoles.map((r, index) => {
                        if (preExistingRoleIds.includes(r.id)) {
                            return (
                                <Dropdown.ListItem key={r.id} onClick={(e) => e?.stopPropagation()}>
                                    <Typography
                                        type="body"
                                        className="oui-label--disabled"
                                        style={{ marginLeft: "12px" }}
                                    >
                                        {getRoleName(r)} (already assigned)
                                    </Typography>
                                </Dropdown.ListItem>
                            );
                        }
                        return (
                            <Dropdown.ListItem key={r.id}>
                                <Dropdown.BlockLink
                                    isLink={r.id !== selectedRole?.id}
                                    isItemSelected={r.id === selectedRole?.id}
                                    onClick={() => onRoleChanged && onRoleChanged(r)}
                                >
                                    {getRoleName(r)}
                                </Dropdown.BlockLink>
                            </Dropdown.ListItem>
                        );
                    })}
                </Dropdown.Contents>
            </Dropdown>
        </div>
    );
};
